import { useCallback, useEffect, useRef, useState } from 'react';

import { widgetConfigUrl } from '../core/config';
import type { ChatMessage, ChatSender, WidgetTypingSignal } from '../types';
import { useTypingWindow } from './use-typing-window';

type UseWidgetChatOptions = {
    widgetKey: string;
    apiOrigin: string;
    enabled: boolean;
};

type ServerMessage = {
    id: number;
    sender: ChatSender;
    text: string;
    created_at: string | null;
};

export type WidgetAppearance = {
    primaryColor: string | null;
    headerBg: string | null;
    headerText: string | null;
    chatBg: string | null;
    position: 'left' | 'right';
    showLogo: boolean;
    logoUrl: string | null;
};

export type WidgetTexts = {
    welcomeMessage: string | null;
    placeholder: string | null;
};

const BASE_INTERVAL_MS = 10000;
const MAX_INTERVAL_MS = 60000;
function newUuid(): string {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
        return globalThis.crypto.randomUUID();
    }

    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(
        '',
    );

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function visitorStorageKey(widgetKey: string): string {
    return `orderpilot_visitor_${widgetKey}`;
}

function getVisitorToken(widgetKey: string): string {
    const key = visitorStorageKey(widgetKey);

    try {
        const existing = window.localStorage.getItem(key);

        if (existing) {
            return existing;
        }
    } catch {
        // Storage unavailable (private mode) — fall through to a fresh token.
    }

    const token = newUuid();

    try {
        window.localStorage.setItem(key, token);
    } catch {
        // Ignore write failures; the session still works without persistence.
    }

    return token;
}

function toChatMessage(message: ServerMessage): ChatMessage {
    return {
        id: `w-${message.id}`,
        sender: message.sender,
        text: message.text,
        timestamp: Date.parse(message.created_at ?? '') || Date.now(),
        status: 'sent',
    };
}

export function useWidgetChat({
    widgetKey,
    apiOrigin,
    enabled,
}: UseWidgetChatOptions) {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [businessName, setBusinessName] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [appearance, setAppearance] = useState<WidgetAppearance>({
        primaryColor: null,
        headerBg: null,
        headerText: null,
        chatBg: null,
        position: 'right',
        showLogo: true,
        logoUrl: null,
    });
    const [texts, setTexts] = useState<WidgetTexts>({
        welcomeMessage: null,
        placeholder: null,
    });
    const [ready, setReady] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const {
        visible: typing,
        sync: syncTyping,
        hide: hideTyping,
    } = useTypingWindow();

    const visitorRef = useRef<string | null>(null);
    const afterIdRef = useRef<number | null>(null);
    const seenIdsRef = useRef<Set<number>>(new Set());
    const timerRef = useRef<number | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const intervalRef = useRef(BASE_INTERVAL_MS);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        let cancelled = false;
        const visitorToken = getVisitorToken(widgetKey);
        visitorRef.current = visitorToken;

        function schedule(): void {
            if (timerRef.current !== null) {
                window.clearTimeout(timerRef.current);
            }

            timerRef.current = window.setTimeout(() => {
                void tick();
            }, intervalRef.current);
        }

        async function tick(): Promise<void> {
            if (cancelled || document.hidden) {
                return;
            }

            const params = new URLSearchParams({
                visitor_token: visitorToken,
            });

            if (afterIdRef.current !== null) {
                params.set('after_id', String(afterIdRef.current));
            }

            const controller = new AbortController();
            abortRef.current = controller;

            try {
                const response = await fetch(
                    `${apiOrigin}/api/widget/${widgetKey}/messages?${params.toString()}`,
                    {
                        headers: { Accept: 'application/json' },
                        signal: controller.signal,
                    },
                );

                if (!response.ok) {
                    throw new Error(
                        `Poll failed with status ${response.status}`,
                    );
                }

                const payload = (await response.json()) as {
                    messages: ServerMessage[];
                } & WidgetTypingSignal;
                const fresh = (payload.messages ?? []).filter(
                    (message) => !seenIdsRef.current.has(message.id),
                );

                if (fresh.length > 0) {
                    for (const message of fresh) {
                        seenIdsRef.current.add(message.id);
                        afterIdRef.current =
                            afterIdRef.current === null
                                ? message.id
                                : Math.max(afterIdRef.current, message.id);
                    }

                    setMessages((prev) => [
                        ...prev,
                        ...fresh.map(toChatMessage),
                    ]);
                }

                const replyArrived = fresh.some(
                    (message) => message.sender === 'shop',
                );

                if (replyArrived) {
                    hideTyping();
                } else {
                    syncTyping(payload);
                }

                intervalRef.current = BASE_INTERVAL_MS;
            } catch {
                if (controller.signal.aborted || cancelled) {
                    return;
                }

                intervalRef.current = Math.min(
                    intervalRef.current * 2,
                    MAX_INTERVAL_MS,
                );
            } finally {
                abortRef.current = null;
            }

            if (!cancelled && !document.hidden) {
                schedule();
            }
        }

        fetch(widgetConfigUrl({ widgetKey, apiOrigin }), {
            headers: { Accept: 'application/json' },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(
                        `Config failed with status ${response.status}`,
                    );
                }

                return response.json() as Promise<{
                    business_name: string;
                    display_name?: string;
                    appearance?: {
                        primary_color?: string;
                        header_bg?: string | null;
                        header_text?: string | null;
                        chat_bg?: string | null;
                        position?: 'left' | 'right';
                        show_logo?: boolean;
                        logo_url?: string | null;
                    };
                    texts?: {
                        welcome_message?: string | null;
                        placeholder?: string | null;
                    };
                }>;
            })
            .then((payload) => {
                if (cancelled) {
                    return;
                }

                setBusinessName(payload.business_name ?? '');
                setDisplayName(
                    payload.display_name ?? payload.business_name ?? '',
                );

                const appearance = payload.appearance;

                if (appearance) {
                    setAppearance({
                        primaryColor: appearance.primary_color ?? null,
                        headerBg: appearance.header_bg ?? null,
                        headerText: appearance.header_text ?? null,
                        chatBg: appearance.chat_bg ?? null,
                        position: appearance.position ?? 'right',
                        showLogo: appearance.show_logo ?? true,
                        logoUrl: appearance.logo_url ?? null,
                    });
                }

                const texts = payload.texts;

                if (texts) {
                    setTexts({
                        welcomeMessage: texts.welcome_message ?? null,
                        placeholder: texts.placeholder ?? null,
                    });
                }

                setReady(true);
                setError(null);
                intervalRef.current = BASE_INTERVAL_MS;
                void tick();
            })
            .catch(() => {
                if (!cancelled) {
                    setError(
                        'Chat is unavailable right now. Please try again later.',
                    );
                }
            });

        const onVisibilityChange = () => {
            if (document.hidden) {
                if (timerRef.current !== null) {
                    window.clearTimeout(timerRef.current);
                    timerRef.current = null;
                }

                abortRef.current?.abort();
                abortRef.current = null;

                return;
            }

            if (!cancelled) {
                void tick();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            cancelled = true;
            document.removeEventListener(
                'visibilitychange',
                onVisibilityChange,
            );

            if (timerRef.current !== null) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }

            abortRef.current?.abort();
            abortRef.current = null;
        };
    }, [enabled, apiOrigin, widgetKey, hideTyping, syncTyping]);

    const send = useCallback(
        async (text: string) => {
            const trimmed = text.trim();

            if (!trimmed || !ready) {
                return;
            }

            const visitorToken =
                visitorRef.current ?? getVisitorToken(widgetKey);
            visitorRef.current = visitorToken;
            const clientId = newUuid();

            setMessages((prev) => [
                ...prev,
                {
                    id: clientId,
                    sender: 'customer',
                    text: trimmed,
                    timestamp: Date.now(),
                    status: 'sending',
                },
            ]);

            try {
                const response = await fetch(
                    `${apiOrigin}/api/widget/${widgetKey}/messages`,
                    {
                        method: 'POST',
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            visitor_token: visitorToken,
                            client_id: clientId,
                            body: trimmed,
                        }),
                    },
                );

                if (!response.ok) {
                    throw new Error(
                        `Send failed with status ${response.status}`,
                    );
                }

                const payload = (await response.json()) as {
                    message: ServerMessage;
                    reply?: ServerMessage;
                } & WidgetTypingSignal;
                const confirmed = toChatMessage(payload.message);
                const reply = payload.reply
                    ? toChatMessage(payload.reply)
                    : null;

                if (reply === null) {
                    syncTyping(payload);
                } else {
                    hideTyping();
                }

                const received = payload.reply
                    ? [payload.message, payload.reply]
                    : [payload.message];

                for (const item of received) {
                    seenIdsRef.current.add(item.id);
                    afterIdRef.current =
                        afterIdRef.current === null
                            ? item.id
                            : Math.max(afterIdRef.current, item.id);
                }

                setMessages((prev) => {
                    const withoutOptimistic = prev.filter(
                        (message) => message.id !== clientId,
                    );
                    const withoutDuplicates = withoutOptimistic.filter(
                        (message) =>
                            message.id !== confirmed.id &&
                            (reply === null || message.id !== reply.id),
                    );

                    return [
                        ...withoutDuplicates,
                        confirmed,
                        ...(reply === null ? [] : [reply]),
                    ];
                });
            } catch {
                hideTyping();
                setMessages((prev) =>
                    prev.map((message) =>
                        message.id === clientId
                            ? { ...message, status: 'failed' as const }
                            : message,
                    ),
                );
            }
        },
        [apiOrigin, widgetKey, ready, hideTyping, syncTyping],
    );

    const retry = useCallback(
        (id: string) => {
            const target = messages.find((message) => message.id === id);

            if (!target || target.status !== 'failed') {
                return;
            }

            setMessages((prev) =>
                prev.map((message) =>
                    message.id === id
                        ? { ...message, status: 'sending' as const }
                        : message,
                ),
            );

            const visitorToken =
                visitorRef.current ?? getVisitorToken(widgetKey);
            visitorRef.current = visitorToken;

            fetch(`${apiOrigin}/api/widget/${widgetKey}/messages`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    visitor_token: visitorToken,
                    client_id: target.id,
                    body: target.text,
                }),
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(
                            `Retry failed with status ${response.status}`,
                        );
                    }

                    return response.json() as Promise<
                        {
                            message: ServerMessage;
                            reply?: ServerMessage;
                        } & WidgetTypingSignal
                    >;
                })
                .then((payload) => {
                    const confirmed = toChatMessage(payload.message);
                    const reply = payload.reply
                        ? toChatMessage(payload.reply)
                        : null;

                    if (reply === null) {
                        syncTyping(payload);
                    } else {
                        hideTyping();
                    }

                    const received = payload.reply
                        ? [payload.message, payload.reply]
                        : [payload.message];

                    for (const item of received) {
                        seenIdsRef.current.add(item.id);
                        afterIdRef.current =
                            afterIdRef.current === null
                                ? item.id
                                : Math.max(afterIdRef.current, item.id);
                    }

                    setMessages((prev) => {
                        const withoutOptimistic = prev.filter(
                            (message) => message.id !== target.id,
                        );
                        const withoutDuplicates = withoutOptimistic.filter(
                            (message) =>
                                message.id !== confirmed.id &&
                                (reply === null || message.id !== reply.id),
                        );

                        return [
                            ...withoutDuplicates,
                            confirmed,
                            ...(reply === null ? [] : [reply]),
                        ];
                    });
                })
                .catch(() => {
                    hideTyping();
                    setMessages((prev) =>
                        prev.map((message) =>
                            message.id === target.id
                                ? { ...message, status: 'failed' as const }
                                : message,
                        ),
                    );
                });
        },
        [apiOrigin, widgetKey, messages, hideTyping, syncTyping],
    );

    const lastReportedReadRef = useRef<number>(0);

    const markRead = useCallback(
        (lastSeenId: number) => {
            if (!ready || lastSeenId <= lastReportedReadRef.current) {
                return;
            }

            lastReportedReadRef.current = lastSeenId;
            const visitorToken =
                visitorRef.current ?? getVisitorToken(widgetKey);
            visitorRef.current = visitorToken;

            fetch(`${apiOrigin}/api/widget/${widgetKey}/read`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    visitor_token: visitorToken,
                    last_seen_id: lastSeenId,
                }),
            }).catch(() => {
                // Read receipts are best-effort; the badge already cleared locally.
            });
        },
        [apiOrigin, widgetKey, ready],
    );

    return {
        messages,
        businessName,
        displayName,
        appearance,
        texts,
        ready,
        error,
        typing,
        send,
        retry,
        markRead,
    };
}
